import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'

interface Props {
  expanded?: boolean
  onChange?: () => void
  children: React.ReactNode
  size?: 's' | 'm' | 'l'
  withTag?: boolean
  withDivider?: boolean
  id?: string
  style?: React.CSSProperties
  className?: string
}

export const Accordion = ({
  expanded = false,
  onChange,
  children,
  size = 'm',
  withDivider = true,
  id,
  className,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (contentRef.current) {
      if (expanded) {
        setHeight(contentRef.current.scrollHeight)
      } else {
        setHeight(0)
      }
    }
  }, [expanded])

  const childrenArray = React.Children.toArray(children) as React.ReactNode[]

  const header = childrenArray.find(
    (child): child is React.ReactElement<{ slot?: string }> =>
      React.isValidElement(child) && (child.props as { slot?: string }).slot === 'header',
  )
  const body = childrenArray.filter(
    child => !(React.isValidElement(child) && (child.props as { slot?: string }).slot === 'header'),
  )

  const textRef = useRef<HTMLDivElement>(null)
  const [showTooltip, setShowTooltip] = useState(false)

  const headerText = React.useMemo(() => {
    if (!React.isValidElement(header)) {
      return ''
    }

    const title = (header.props as { title?: string }).title
    if (typeof title === 'string' && title.trim().length > 0) {
      return title
    }

    return React.Children.toArray((header.props as { children?: React.ReactNode }).children)
      .filter((item): item is React.ReactElement => React.isValidElement(item))
      .map(item => React.Children.toArray(item.props.children).join(''))
      .join('')
  }, [header])

  useEffect(() => {
    if (headerText.length > 80) {
      setShowTooltip(true)
    }
  }, [headerText])

  return (
    <div id={id} className={clsx('w-full px-6,', withDivider && 'border-solid border-b border-[#e7eaee]', className)}>
      <div className={clsx('relative')}>
        <button
          type="button"
          onClick={onChange}
          className={clsx('w-full flex items-center justify-between text-left', {
            'py-3': size === 's',
            'py-4': size === 'm',
            'py-5': size === 'l',
          })}
        >
          <div className="w-full group">
            <div ref={textRef} className="w-full overflow-hidden">
              {header}
            </div>

            {showTooltip && (
              <div className="absolute -translate-x-1/2 left-1/2 bottom-[90%] mt-2 hidden group-hover:block z-[9999]">
                <div className="bg-[#4A4A4A] text-white text-sm px-2 py-2 rounded shadow-lg max-w-[350px] break-words">
                  {typeof headerText === 'string' ? headerText : ''}
                  <div
                    className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0
                    border-solid
                    border-l-[6px] border-l-transparent 
                    border-r-[6px] border-r-transparent 
                    border-t-[6px] border-t-[#4A4A4A]"
                  />
                </div>
              </div>
            )}
          </div>
        </button>
      </div>

      <div
        style={{
          height,
          transition: 'height 400ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        className="overflow-hidden"
      >
        <div ref={contentRef}>
          <div className="flex flex-col gap-4 w-full">{body}</div>
        </div>
      </div>
    </div>
  )
}
